/**
 * PATH: apps/quote/src/components/builder/panels/assumptions-content-parser.ts
 * FILE_ID: QB-FE-ASSUMPTIONS-PARSER-001
 * PURPOSE: Pure parsers for Quote.config.{assumptions,wagesBuildUp,
 *          productSummary} → typed UI shapes. Defensive: missing blobs
 *          return null, never throw.
 * RELATED: QB-BRAVADA-ASSUMPTIONS-SEED-001 (backend source of truth)
 */

export interface PerLineComment {
  section: string
  lineItem: string
  comment: string
}

export interface WorksProgramZone {
  zone: string
  start: string
  end: string
  weeks: number
  weeksPre: number
  weeksPost: number
  percentPost: number
}

export interface WorksProgram {
  zones: WorksProgramZone[]
  totalWeeks: number | null
  totalDays: number | null
  percentRequiringEscalation: number | null
}

export interface GroutCompartmentRow {
  typeLabel: string
  number: number
  hoseLengthPerUnit: number
  hoseLengthTotal: number
  boxesPerCompartment: number
  boxesTotal: number
  flangesPerCompartment: number
  flangesTotal: number
}

export interface GroutCompartmentSection {
  rows: GroutCompartmentRow[]
  totals: { hoseLength: number; boxes: number; flanges: number }
}

export interface GroutCompartments {
  dpk4705: GroutCompartmentSection | null
  dpk4706: GroutCompartmentSection | null
  grandTotals: { hoseLength: number; boxes: number; flanges: number } | null
}

/**
 * MR E (typed Assumptions): provenance — captures which Excel workbook
 * and worksheet the typed assumption blob was transcribed from. The
 * backend seed writes _source + _sheet into Quote.config.assumptions
 * so the frontend can show estimators "this number came from
 * Q9525R10_main.xlsm, Assumptions sheet" rather than presenting it as
 * an opaque value. Optional — null when the quote was authored in the
 * builder (no Excel origin).
 */
export interface Provenance {
  source: string
  sheet: string
}

export interface AssumptionsContent {
  perLineComments: PerLineComment[]
  worksProgram: WorksProgram | null
  groutCompartments: GroutCompartments | null
  /** @deprecated kept for back-compat — use `provenance.source` */
  source: string | null
  provenance: Provenance | null
}

export interface WagesRow {
  label: string
  hoursPerDay: number | null
  costPerHour: number | null
  costPerDay: number | null
  chargeoutPerHour: number | null
  chargeoutPerDay: number | null
  grossMarginDollars: number | null
  grossMarginPercent: number | null
}

export interface WagesEscalation {
  escalationPost: number | null
  increaseInLabourCost: number | null
  byPercentOfProjectPost: number | null
  increaseInLabourCostApportioned: number | null
}

export interface WagesBuildUp {
  buildUp: WagesRow[]
  escalation: WagesEscalation | null
  revised: WagesRow | null
  revisedSay: WagesRow | null
  finalRateApplied: number | null
}

/**
 * MR F (Product Summary supplier grouping): each row in the
 * SupplierGroup carries the supplier's name, the materials it covers,
 * and the per-supplier roll-up of `both` quantity. Drives the
 * supplier-grouped layout in ProductSummaryPanel — estimators see
 * "Sika Australia — 9 materials, total $X" rather than 14 ungrouped
 * lines. Empty array when no `suppliers` map is present on the blob.
 */
export interface SupplierGroup {
  supplier: string
  materials: string[]
  totalBoth: number
  totalDpk4705: number
  totalDpk4706: number
}

export interface ProductSummary {
  materials: string[]
  totals: {
    dpk4705: Record<string, number>
    dpk4706: Record<string, number>
    both: Record<string, number>
  }
  suppliers: Record<string, string>
  bySupplier: SupplierGroup[]
  grandTotals: { dpk4705: number; dpk4706: number; both: number }
}

export function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'string' ? parseFloat(v) : Number(v)
  return Number.isFinite(n) ? n : null
}

function toRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>
  }
  return null
}

function toArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : []
}

function toString(v: unknown): string {
  return v === null || v === undefined ? '' : String(v)
}

function toNumberOrZero(v: unknown): number {
  return toNumber(v) ?? 0
}

export function parseAssumptionsContent(
  config: unknown,
): AssumptionsContent | null {
  const cfg = toRecord(config)
  if (!cfg) return null
  const blob = toRecord(cfg.assumptions)
  if (!blob) return null

  const perLineComments: PerLineComment[] = toArray(blob.perLineComments).map(
    (raw) => {
      const r = toRecord(raw) ?? {}
      return {
        section: toString(r.section),
        lineItem: toString(r.line_item),
        comment: toString(r.comment),
      }
    },
  )

  const worksProgram = parseWorksProgram(blob.worksProgram)
  const groutCompartments = parseGroutCompartments(blob.groutCompartments)

  const sourceStr = toString(blob._source) || null
  const sheetStr = toString(blob._sheet) || null
  const provenance: Provenance | null =
    sourceStr && sheetStr ? { source: sourceStr, sheet: sheetStr } : null
  return {
    perLineComments,
    worksProgram,
    groutCompartments,
    source: sourceStr,
    provenance,
  }
}

function parseWorksProgram(raw: unknown): WorksProgram | null {
  const wp = toRecord(raw)
  if (!wp) return null
  const zones: WorksProgramZone[] = toArray(wp.zones).map((z) => {
    const r = toRecord(z) ?? {}
    return {
      zone: toString(r.zone),
      start: toString(r.start),
      end: toString(r.end),
      weeks: toNumberOrZero(r.weeks),
      weeksPre: toNumberOrZero(r.weeks_pre_2025_07),
      weeksPost: toNumberOrZero(r.weeks_post_2025_07),
      percentPost: toNumberOrZero(r.percent_post),
    }
  })
  return {
    zones,
    totalWeeks: toNumber(wp.totalWeeks),
    totalDays: toNumber(wp.totalDays),
    percentRequiringEscalation: toNumber(wp.percentRequiringEscalation),
  }
}

function parseGroutCompartments(raw: unknown): GroutCompartments | null {
  const gc = toRecord(raw)
  if (!gc) return null
  return {
    dpk4705: parseGroutSection(gc.dpk4705),
    dpk4706: parseGroutSection(gc.dpk4706),
    grandTotals: parseGroutTotals(gc.grandTotals),
  }
}

function parseGroutSection(raw: unknown): GroutCompartmentSection | null {
  const s = toRecord(raw)
  if (!s) return null
  const rows: GroutCompartmentRow[] = toArray(s.rows).map((r) => {
    const x = toRecord(r) ?? {}
    return {
      typeLabel: toString(x.type_label),
      number: toNumberOrZero(x.number),
      hoseLengthPerUnit: toNumberOrZero(x.hose_length_per_unit),
      hoseLengthTotal: toNumberOrZero(x.hose_length_total),
      boxesPerCompartment: toNumberOrZero(x.boxes_per_compartment),
      boxesTotal: toNumberOrZero(x.boxes_total),
      flangesPerCompartment: toNumberOrZero(x.flanges_per_compartment),
      flangesTotal: toNumberOrZero(x.flanges_total),
    }
  })
  return { rows, totals: parseGroutTotals(s.totals) ?? { hoseLength: 0, boxes: 0, flanges: 0 } }
}

function parseGroutTotals(
  raw: unknown,
): { hoseLength: number; boxes: number; flanges: number } | null {
  const t = toRecord(raw)
  if (!t) return null
  return {
    hoseLength: toNumberOrZero(t.hose_length_total),
    boxes: toNumberOrZero(t.boxes_total),
    flanges: toNumberOrZero(t.flanges_total),
  }
}

export function parseWagesBuildUp(config: unknown): WagesBuildUp | null {
  const cfg = toRecord(config)
  if (!cfg) return null
  const blob = toRecord(cfg.wagesBuildUp)
  if (!blob) return null

  const buildUp: WagesRow[] = toArray(blob.buildUp).map(parseWagesRow)
  const escRecord = toRecord(blob.escalation)
  const escalation: WagesEscalation | null = escRecord
    ? {
        escalationPost: toNumber(escRecord.escalation_post_2025_07),
        increaseInLabourCost: toNumber(escRecord.increase_in_labour_cost),
        byPercentOfProjectPost: toNumber(
          escRecord.by_percent_of_project_post_2025_07,
        ),
        increaseInLabourCostApportioned: toNumber(
          escRecord.increase_in_labour_cost_apportioned,
        ),
      }
    : null

  const revised = blob.revised ? parseWagesRow(blob.revised) : null
  const revisedSay = blob.revisedSay ? parseWagesRow(blob.revisedSay) : null
  return {
    buildUp,
    escalation,
    revised,
    revisedSay,
    finalRateApplied: toNumber(blob.finalRateApplied),
  }
}

function parseWagesRow(raw: unknown): WagesRow {
  const r = toRecord(raw) ?? {}
  return {
    label: toString(r.label),
    hoursPerDay: toNumber(r.hours_per_day),
    costPerHour: toNumber(r.cost_per_hour),
    costPerDay: toNumber(r.cost_per_day),
    chargeoutPerHour: toNumber(r.chargeout_per_hour),
    chargeoutPerDay: toNumber(r.chargeout_per_day),
    grossMarginDollars: toNumber(r.gross_margin_dollars),
    grossMarginPercent: toNumber(r.gross_margin_percent),
  }
}

export function parseProductSummary(config: unknown): ProductSummary | null {
  const cfg = toRecord(config)
  if (!cfg) return null
  const blob = toRecord(cfg.productSummary)
  if (!blob) return null

  const materials: string[] = toArray(blob.materials).map(toString)
  const totals = toRecord(blob.totals) ?? {}
  const toTotalsMap = (raw: unknown): Record<string, number> => {
    const r = toRecord(raw) ?? {}
    const out: Record<string, number> = {}
    for (const k of Object.keys(r)) {
      out[k] = toNumberOrZero(r[k])
    }
    return out
  }
  const totalsDpk4705 = toTotalsMap(totals.dpk4705)
  const totalsDpk4706 = toTotalsMap(totals.dpk4706)
  const totalsBoth = toTotalsMap(totals.both)
  // Suppliers map is optional — older quotes were seeded before MR F
  // landed. When missing, every material lands in an implicit
  // "Uncategorised" bucket so the table layout stays valid.
  const suppliersRec = toRecord(blob.suppliers) ?? {}
  const suppliers: Record<string, string> = {}
  for (const k of Object.keys(suppliersRec)) {
    suppliers[k] = toString(suppliersRec[k])
  }
  // Group materials by supplier — Map preserves insertion order so the
  // first supplier seen in the materials list anchors the first group.
  const groups = new Map<string, SupplierGroup>()
  for (const mat of materials) {
    const supplier = suppliers[mat] || 'Uncategorised'
    if (!groups.has(supplier)) {
      groups.set(supplier, {
        supplier,
        materials: [],
        totalBoth: 0,
        totalDpk4705: 0,
        totalDpk4706: 0,
      })
    }
    const g = groups.get(supplier)!
    g.materials.push(mat)
    g.totalBoth += totalsBoth[mat] ?? 0
    g.totalDpk4705 += totalsDpk4705[mat] ?? 0
    g.totalDpk4706 += totalsDpk4706[mat] ?? 0
  }
  // Grand totals roll across every material — same numbers Excel shows
  // on the bottom row of the Product Summary sheet. Computed here so
  // the panel doesn't have to re-sum on every render.
  const grandTotals = {
    dpk4705: materials.reduce((s, m) => s + (totalsDpk4705[m] ?? 0), 0),
    dpk4706: materials.reduce((s, m) => s + (totalsDpk4706[m] ?? 0), 0),
    both: materials.reduce((s, m) => s + (totalsBoth[m] ?? 0), 0),
  }
  return {
    materials,
    totals: {
      dpk4705: totalsDpk4705,
      dpk4706: totalsDpk4706,
      both: totalsBoth,
    },
    suppliers,
    bySupplier: Array.from(groups.values()),
    grandTotals,
  }
}
